import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar, Target, Plus, Trash2, CheckCircle, Clock, Zap, BookOpen, FileText, Award,
  CalendarDays, CalendarRange, LayoutList, ChevronRight, Check, Square, PlusCircle, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanPage() {
  const { getAuthHeader, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [actionPlan, setActionPlan] = useState(null);
  const [roadmap90Day, setRoadmap90Day] = useState(null);
  const [roadmap12Month, setRoadmap12Month] = useState(null);
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', target_date: '', category: 'general' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('7day');
  const [completedTasks, setCompletedTasks] = useState(new Set());

  useEffect(() => {
    fetchMilestones();
    loadSavedPlans();
    loadCompletedTasks();
  }, []);

  const loadSavedPlans = async () => {
    try {
      const response = await axios.get(`${API}/user-data`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      const data = response.data;
      if (data.action_plan?.data) setActionPlan(data.action_plan.data);
      if (data.roadmap_90day?.data) setRoadmap90Day(data.roadmap_90day.data);
      if (data.roadmap_12month?.data) setRoadmap12Month(data.roadmap_12month.data);
    } catch (error) {
      console.error('Failed to load saved plans:', error);
    }
  };

  const loadCompletedTasks = async () => {
    try {
      const response = await axios.get(`${API}/action-tasks/completed`, getAuthHeader());
      setCompletedTasks(new Set(response.data.completed_task_ids || []));
    } catch (error) {
      console.error('Failed to load completed tasks:', error);
    }
  };

  const toggleTaskComplete = async (taskId, taskTitle, taskDescription, taskDay) => {
    const isCompleted = completedTasks.has(taskId);
    
    try {
      if (isCompleted) {
        // Unmark task and remove from milestones
        await axios.delete(`${API}/action-tasks/${taskId}/complete`, getAuthHeader());
        setCompletedTasks(prev => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
        
        // Also remove from milestones
        try {
          await axios.delete(`${API}/milestones/by-title/${encodeURIComponent('✓ ' + taskTitle)}`, getAuthHeader());
        } catch (e) {
          // Milestone may not exist, that's okay
        }
        
        toast.success('Task unmarked & removed from milestones');
      } else {
        // Mark task as complete
        await axios.post(`${API}/action-tasks/${taskId}/complete`, { title: taskTitle }, getAuthHeader());
        setCompletedTasks(prev => new Set([...prev, taskId]));
        
        // Auto-add to milestones as completed
        try {
          const targetDate = new Date();
          await axios.post(`${API}/milestones`, null, {
            ...getAuthHeader(),
            withCredentials: true,
            params: {
              title: `✓ ${taskTitle}`,
              description: taskDescription || 'Completed from Action Plan',
              target_date: targetDate.toISOString().split('T')[0],
              category: 'completed'
            }
          });
        } catch (e) {
          // Silently fail milestone add - task is still marked complete
          console.log('Milestone auto-add skipped');
        }
        
        toast.success('Task completed & added to milestones! 🎉');
      }
      
      // Refresh milestones list
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const addTaskToMilestones = async (task) => {
    try {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + (task.day || 7));
      
      const response = await axios.post(`${API}/milestones`, null, {
        ...getAuthHeader(),
        withCredentials: true,
        params: {
          title: task.title,
          description: task.description,
          target_date: targetDate.toISOString().split('T')[0],
          category: 'academic'
        }
      });
      setMilestones([...milestones, response.data]);
      toast.success('Added to milestones!');
    } catch (error) {
      toast.error('Failed to add milestone');
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/milestones`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setMilestones(response.data);
    } catch (error) {
      console.error('Failed to fetch milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const generate7DayPlan = async () => {
    setGenerating('7day');
    try {
      const response = await axios.post(`${API}/ai/action-plan?regenerate=${!!actionPlan}`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setActionPlan(response.data.result);
      refreshUser();
      toast.success('7-day plan generated!');
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to generate plan' });
    } finally {
      setGenerating(null);
    }
  };

  const generate90DayPlan = async () => {
    setGenerating('90day');
    try {
      const response = await axios.post(`${API}/ai/roadmap-90day?regenerate=${!!roadmap90Day}`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 90000
      });
      setRoadmap90Day(response.data.result);
      refreshUser();
      toast.success('90-day roadmap generated!');
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to generate 90-day plan' });
    } finally {
      setGenerating(null);
    }
  };

  const generate12MonthPlan = async () => {
    setGenerating('12month');
    try {
      const response = await axios.post(`${API}/ai/roadmap-12month?regenerate=${!!roadmap12Month}`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 90000
      });
      setRoadmap12Month(response.data.result);
      refreshUser();
      toast.success('12-month roadmap generated!');
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to generate 12-month plan' });
    } finally {
      setGenerating(null);
    }
  };

  const addMilestone = async () => {
    if (!newMilestone.title || !newMilestone.target_date) {
      toast.error('Please fill in title and target date');
      return;
    }
    try {
      const response = await axios.post(`${API}/milestones`, null, {
        ...getAuthHeader(),
        withCredentials: true,
        params: newMilestone
      });
      setMilestones([...milestones, response.data]);
      setNewMilestone({ title: '', description: '', target_date: '', category: 'general' });
      setShowAddForm(false);
      toast.success('Milestone added!');
    } catch (error) {
      toast.error('Failed to add milestone');
    }
  };

  const toggleMilestone = async (id) => {
    try {
      await axios.put(`${API}/milestones/${id}/complete`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setMilestones(milestones.map(m => m.id === id ? { ...m, completed: true } : m));
      toast.success('Milestone completed! 🎉');
    } catch (error) {
      toast.error('Failed to update milestone');
    }
  };

  const deleteMilestone = async (id) => {
    try {
      await axios.delete(`${API}/milestones/${id}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setMilestones(milestones.filter(m => m.id !== id));
      toast.success('Milestone deleted');
    } catch (error) {
      toast.error('Failed to delete milestone');
    }
  };

  const categoryIcons = {
    academics: BookOpen,
    applications: FileText,
    activities: Award,
    'test-prep': Target,
    general: Calendar,
    academic: BookOpen,
    career: Target,
    personal: Award,
    financial: FileText
  };

  const tabs = [
    { id: '7day', label: '7-Day', icon: LayoutList },
    { id: '90day', label: '90-Day', icon: CalendarDays },
    { id: '12month', label: '12-Month', icon: CalendarRange },
  ];

  return (
    <AppLayout>
      <PageHeader 
        title="Action Plans & Roadmaps"
        description="Track your progress with AI-powered planning"
      />

      {/* Plan Type Tabs */}
      <div className="flex gap-2 mb-6 mt-4">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            className={activeTab === tab.id ? 'bg-blue-600' : 'border-slate-700 text-slate-300'}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
        <Button 
          onClick={() => setShowAddForm(true)}
          variant="outline"
          className="ml-auto border-slate-700 text-slate-300"
          data-testid="add-milestone-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Milestone
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Plan Area */}
        <div className="lg:col-span-2">
          {/* 7-Day Plan Tab */}
          {activeTab === '7day' && (
            <GlassCard>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit text-xl font-semibold text-white">7-Day Action Plan</h2>
                <Button
                  onClick={generate7DayPlan}
                  disabled={generating === '7day'}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-7day-btn"
                >
                  {generating === '7day' ? <LoadingSpinner size="sm" /> : <Zap className="w-4 h-4 mr-2" />}
                  {actionPlan ? 'Regenerate' : 'Generate'}
                </Button>
              </div>

              {actionPlan ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-600/10 rounded-lg border border-blue-500/20">
                    <p className="text-blue-400 font-medium">{actionPlan.week_focus}</p>
                    {actionPlan.weekly_goal && (
                      <p className="text-slate-300 text-sm mt-1">Goal: {actionPlan.weekly_goal}</p>
                    )}
                    {/* Progress indicator */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-300">Progress</span>
                        <span className="text-blue-400">
                          {actionPlan.actions?.filter((_, i) => completedTasks.has(`7day-${i}`)).length || 0} / {actionPlan.actions?.length || 0}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ 
                            width: `${(actionPlan.actions?.filter((_, i) => completedTasks.has(`7day-${i}`)).length || 0) / (actionPlan.actions?.length || 1) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-300 mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Click the day button on each task to mark it complete
                    </p>
                  </div>

                  <div className="space-y-3">
                    {actionPlan.actions?.map((action, index) => {
                      const taskId = `7day-${index}`;
                      const isCompleted = completedTasks.has(taskId);
                      
                      return (
                        <div 
                          key={index} 
                          className={`flex items-start gap-4 p-4 rounded-lg transition-all cursor-pointer group ${
                            isCompleted 
                              ? 'bg-emerald-500/10 border border-emerald-500/30' 
                              : 'bg-slate-800/30 hover:bg-slate-800/50 border border-transparent hover:border-blue-500/30'
                          }`}
                          onClick={() => toggleTaskComplete(taskId, action.title, action.description, action.day)}
                        >
                          {/* Clickable checkbox */}
                          <button
                            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                              isCompleted 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-slate-700/50 group-hover:bg-blue-500 group-hover:text-white text-slate-300'
                            }`}
                            title={isCompleted ? 'Click to unmark' : 'Click to complete'}
                          >
                            {isCompleted ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              <Square className="w-5 h-5 group-hover:hidden" />
                            )}
                            {!isCompleted && <Check className="w-5 h-5 hidden group-hover:block" />}
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">Day {action.day}</span>
                              <h3 className={`font-medium ${isCompleted ? 'text-emerald-400 line-through' : 'text-white'}`}>
                                {action.title}
                              </h3>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                action.priority === 'high' ? 'bg-amber-500/20 text-amber-400' :
                                action.priority === 'medium' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-700 text-slate-300'
                              }`}>
                                {action.priority}
                              </span>
                              {isCompleted && (
                                <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">
                                  ✓ Done
                                </span>
                              )}
                            </div>
                            <p className={`text-sm ${isCompleted ? 'text-slate-300' : 'text-slate-300'}`}>
                              {action.description}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-300">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {action.time_estimate}
                              </span>
                              {!isCompleted && (
                                <button
                                  onClick={() => addTaskToMilestones({ ...action, day: action.day })}
                                  className="flex items-center gap-1 text-purple-400 hover:text-purple-300"
                                >
                                  <PlusCircle className="w-3 h-3" />
                                  Add to Milestones
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-300">
                  <Target className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">No plan generated yet</h3>
                  <p className="text-sm">Generate an AI-powered weekly action plan</p>
                </div>
              )}
            </GlassCard>
          )}

          {/* 90-Day Plan Tab */}
          {activeTab === '90day' && (
            <GlassCard>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit text-xl font-semibold text-white">90-Day Roadmap</h2>
                <Button
                  onClick={generate90DayPlan}
                  disabled={generating === '90day'}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="generate-90day-btn"
                >
                  {generating === '90day' ? <LoadingSpinner size="sm" /> : <Zap className="w-4 h-4 mr-2" />}
                  {roadmap90Day ? 'Regenerate' : 'Generate'}
                </Button>
              </div>

              {roadmap90Day ? (
                <div className="space-y-6">
                  <div className="p-4 bg-emerald-600/10 rounded-lg border border-emerald-500/20">
                    <p className="text-emerald-400 font-medium text-lg">{roadmap90Day.quarter_focus}</p>
                  </div>

                  {/* Months */}
                  {roadmap90Day.months?.map((month, mIndex) => (
                    <div key={mIndex} className="border border-slate-700/50 rounded-lg overflow-hidden">
                      <div className="p-4 bg-slate-800/50">
                        <h3 className="font-outfit font-semibold text-white">Month {month.month}: {month.theme}</h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {month.goals?.map((goal, gIndex) => (
                            <span key={gIndex} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">
                              {goal}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {month.weeks?.map((week, wIndex) => (
                        <div key={wIndex} className="p-3 border-t border-slate-700/30">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                              Week {week.week}
                            </span>
                            <span className="text-sm text-slate-300">{week.focus}</span>
                          </div>
                          <div className="grid gap-2">
                            {week.tasks?.slice(0, 3).map((task, tIndex) => (
                              <div key={tIndex} className="flex items-center gap-2 text-sm">
                                <ChevronRight className="w-3 h-3 text-slate-300" />
                                <span className="text-slate-300">{task.title}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  task.priority === 'high' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-300'
                                }`}>
                                  {task.estimated_hours}h
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Key Milestones */}
                  {roadmap90Day.key_milestones && (
                    <div className="p-4 bg-slate-800/30 rounded-lg">
                      <h4 className="font-medium text-white mb-3">Key Milestones</h4>
                      <div className="space-y-2">
                        {roadmap90Day.key_milestones.map((milestone, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span className="text-slate-300 text-sm">{milestone.title}</span>
                            <span className="text-xs text-slate-300">{milestone.target_date}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-300">
                  <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">No 90-day roadmap yet</h3>
                  <p className="text-sm">Generate a quarterly action plan with monthly breakdown</p>
                </div>
              )}
            </GlassCard>
          )}

          {/* 12-Month Plan Tab */}
          {activeTab === '12month' && (
            <GlassCard>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit text-xl font-semibold text-white">12-Month Strategic Roadmap</h2>
                <Button
                  onClick={generate12MonthPlan}
                  disabled={generating === '12month'}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-12month-btn"
                >
                  {generating === '12month' ? <LoadingSpinner size="sm" /> : <Zap className="w-4 h-4 mr-2" />}
                  {roadmap12Month ? 'Regenerate' : 'Generate'}
                </Button>
              </div>

              {roadmap12Month ? (
                <div className="space-y-6">
                  <div className="p-4 bg-purple-600/10 rounded-lg border border-purple-500/20">
                    <p className="text-purple-400 font-medium text-lg">{roadmap12Month.year_vision}</p>
                    {roadmap12Month.success_definition && (
                      <p className="text-slate-300 text-sm mt-2">Success: {roadmap12Month.success_definition}</p>
                    )}
                  </div>

                  {/* Quarters */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {roadmap12Month.quarters?.map((quarter, qIndex) => (
                      <div key={qIndex} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-sm font-medium">
                            Q{quarter.quarter}
                          </span>
                          <span className="text-white font-medium">{quarter.name?.replace(/Q\d:\s*/, '')}</span>
                        </div>
                        <p className="text-slate-300 text-sm mb-3">{quarter.primary_focus}</p>
                        
                        <div className="space-y-2">
                          {quarter.key_actions?.slice(0, 3).map((action, aIndex) => (
                            <div key={aIndex} className="flex items-center gap-2 text-sm">
                              <ChevronRight className="w-3 h-3 text-purple-400" />
                              <span className="text-slate-300">{action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Annual Milestones */}
                  {roadmap12Month.annual_milestones && (
                    <div className="p-4 bg-slate-800/30 rounded-lg">
                      <h4 className="font-medium text-white mb-3">Annual Milestones</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {roadmap12Month.annual_milestones.map((milestone, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 bg-slate-700/30 rounded">
                            <Target className="w-4 h-4 text-purple-400 mt-0.5" />
                            <div>
                              <p className="text-white text-sm font-medium">{milestone.milestone}</p>
                              <p className="text-xs text-slate-300">{milestone.quarter} - {milestone.importance}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-300">
                  <CalendarRange className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="font-outfit text-lg font-semibold text-white mb-2">No 12-month roadmap yet</h3>
                  <p className="text-sm">Generate a strategic year-long plan with quarterly goals</p>
                </div>
              )}
            </GlassCard>
          )}
        </div>

        {/* Milestones Sidebar */}
        <div className="space-y-6">
          {showAddForm && (
            <GlassCard>
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">Add Milestone</h3>
              <div className="space-y-4">
                <Input
                  placeholder="Milestone title"
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                  className="input-dark"
                />
                <Input
                  placeholder="Description (optional)"
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  className="input-dark"
                />
                <Input
                  type="date"
                  value={newMilestone.target_date}
                  onChange={(e) => setNewMilestone({ ...newMilestone, target_date: e.target.value })}
                  className="input-dark"
                />
                <Select
                  value={newMilestone.category}
                  onValueChange={(value) => setNewMilestone({ ...newMilestone, category: value })}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="academics">Academics</SelectItem>
                    <SelectItem value="applications">Applications</SelectItem>
                    <SelectItem value="activities">Activities</SelectItem>
                    <SelectItem value="test-prep">Test Prep</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button onClick={addMilestone} className="btn-primary flex-1">Add</Button>
                  <Button onClick={() => setShowAddForm(false)} variant="outline" className="border-slate-700 text-slate-300">
                    Cancel
                  </Button>
                </div>
              </div>
            </GlassCard>
          )}

          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">
              Milestones ({milestones.filter(m => m.completed).length}/{milestones.length})
            </h3>
            
            {loading ? (
              <div className="flex justify-center py-8"><LoadingSpinner /></div>
            ) : milestones.length === 0 ? (
              <div className="text-center py-8 text-slate-300">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No milestones yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {milestones.map((milestone) => (
                  <div 
                    key={milestone.id} 
                    className={`p-3 rounded-lg border ${
                      milestone.completed 
                        ? 'bg-emerald-500/10 border-emerald-500/20' 
                        : 'bg-slate-800/30 border-slate-700/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => !milestone.completed && toggleMilestone(milestone.id)}
                        className={`mt-1 ${milestone.completed ? 'text-emerald-400' : 'text-slate-300 hover:text-blue-400'}`}
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${milestone.completed ? 'text-emerald-400 line-through' : 'text-white'}`}>
                          {milestone.title}
                        </p>
                        <p className="text-slate-300 text-xs mt-1">
                          {new Date(milestone.target_date).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteMilestone(milestone.id)}
                        className="text-slate-300 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>

    </AppLayout>
  );
}
